// §K — the payment receipt at /crew/:slug?receipt={ref}. A RECORD of an
// off-platform payment (cash / e-Transfer) — no Stripe anywhere here (§6.11).
// Download = the print stylesheet.

import { Download } from 'lucide-react';
import { portalColors, portalShadow, portalRadius } from './portalStyles';
import type { CrewReceipt } from '../../types/crewPortal';

interface Props {
  receipt: CrewReceipt;
  onDone: () => void;
}

export function ReceiptView({ receipt, onDone }: Props) {
  const accent = receipt.accent_color || portalColors.defaultAccent;
  const amount = receipt.amount_cents != null ? `$${(receipt.amount_cents / 100).toFixed(2)}` : '—';
  const date = new Date(receipt.paid_at).toLocaleDateString('en-CA', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
  const method = receipt.paid_method === 'cash' ? 'Cash' : 'e-Transfer';

  return (
    <div>
      <style>{`@media print { .receipt-actions { display: none !important; } }`}</style>
      <div style={{
        background: portalColors.card, borderRadius: portalRadius.card,
        boxShadow: portalShadow.card, padding: 24, position: 'relative', overflow: 'hidden',
      }}>
        {/* PAID stamp */}
        <div style={{
          position: 'absolute', top: 18, right: -34, transform: 'rotate(35deg)',
          background: '#2E7D55', color: '#fff', fontSize: 12, fontWeight: 800,
          letterSpacing: 2, padding: '5px 40px',
        }}>PAID</div>

        {/* business mark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {receipt.logo_url ? (
            <img src={receipt.logo_url} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: 8, background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>
              {receipt.business_name.charAt(0)}
            </div>
          )}
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: portalColors.primary }}>{receipt.business_name}</div>
            <div style={{ fontSize: 12, color: portalColors.muted }}>Payment receipt</div>
          </div>
        </div>

        {/* amount */}
        <div style={{ textAlign: 'center', margin: '28px 0 24px' }}>
          <div style={{ fontSize: 40, fontWeight: 700, color: portalColors.primary, letterSpacing: -1 }}>{amount}</div>
          <div style={{ fontSize: 14, color: portalColors.secondary, marginTop: 4 }}>to {receipt.first_name}</div>
        </div>

        {/* rows */}
        {[
          ['For', `${receipt.job_title}${receipt.property ? ` · ${receipt.property}` : ''}`],
          ['Date', date],
          ['Method', method],
          ['Reference', receipt.receipt_ref],
        ].map(([k, v], i) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0',
            borderTop: i === 0 ? `1px solid ${portalColors.divider}` : `0.5px solid ${portalColors.subtleDivider}`,
          }}>
            <span style={{ fontSize: 13, color: portalColors.secondary }}>{k}</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: portalColors.primary, textAlign: 'right' }}>{v}</span>
          </div>
        ))}

        {/* actions */}
        <div className="receipt-actions" style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={() => window.print()} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            height: 46, borderRadius: portalRadius.button, border: `1.5px solid ${portalColors.divider}`,
            background: 'transparent', color: portalColors.primary, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <Download size={16} /> Download
          </button>
          <button onClick={onDone} style={{
            flex: 1, height: 46, borderRadius: portalRadius.button, border: 'none',
            background: accent, color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Done
          </button>
        </div>
      </div>

      <div style={{ textAlign: 'center', fontSize: 12, color: portalColors.muted, marginTop: 16 }}>
        Powered by CrewBit
      </div>
    </div>
  );
}
